import AddIcon from "@mui/icons-material/AddRounded";
import { Button, Divider, List, Stack, Typography } from "@mui/material";
import deepEqual from "fast-deep-equal";
import { useState } from "react";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import DragAndDropList from "../DragAndDropList";
import { useFilterContext } from "../filters/FilterContext";
import { updateColumnsAction } from "../filters/filterState";
import GlobalDndProvider from "../GlobalDndProvider";
import AddColumnSelect from "./AddColumnSelect";
import type { TableColumnDefinition } from "./columnDefinitions";
import ColumnSelectionItem from "./ColumnSelectionItem";

interface Props {
  onClose: () => void;
}

interface ColumnsState {
  visible: TableColumnDefinition[];
  available: TableColumnDefinition[];
}

const ColumnSelectionEditor = ({ onClose }: Props) => {
  const { filterState, setFilterState } = useFilterContext();

  const [columns, setColumns] = useState<ColumnsState>({
    visible: filterState.visibleColumns,
    available: filterState.availableColumns,
  });

  const [isAddingColumn, setIsAddingColumn] = useState(false);

  const handleApply = () => {
    onClose();
    setTimeout(() => setFilterState(updateColumnsAction(columns.visible, columns.available)), 100);
  };

  const handleCancel = () => {
    onClose();
    setTimeout(() => setColumns({ visible: filterState.visibleColumns, available: filterState.availableColumns }), 100);
  };

  const handleItemDrop = (dropIndex: number, draggedColumn: TableColumnDefinition | undefined) => {
    if (!draggedColumn) {
      return;
    }

    setColumns(({ visible, available }) => {
      const newVisible = visible.filter((col) => col.id !== draggedColumn.id);
      const insertIndex = dropIndex < 0 ? newVisible.length : dropIndex;
      newVisible.splice(insertIndex, 0, draggedColumn);
      return { visible: newVisible, available };
    });
  };

  const handleHideColumn = (columnId: string) => () => {
    setColumns(({ visible, available }) => {
      const columnToHide = visible.find((col) => col.id === columnId);
      if (!columnToHide) {
        return { visible, available };
      }

      return {
        visible: visible.filter((col) => col.id !== columnId),
        available: [...available, columnToHide],
      };
    });
  };

  const handleAddColumn = (columnId: string) => {
    setIsAddingColumn(false);
    setColumns(({ visible, available }) => {
      const columnToAdd = available.find((col) => col.id === columnId);
      if (!columnToAdd) {
        return { visible, available };
      }

      return {
        visible: [...visible, columnToAdd],
        available: available.filter((col) => col.id !== columnId),
      };
    });
  };

  const isDirty = !deepEqual(columns.visible, filterState.visibleColumns);

  return (
    <Stack
      sx={[{ overflow: "hidden", alignItems: "flex-start" }, (t) => ({ width: t.spacing(52.5), maxHeight: "80vh" })]}
    >
      <Stack
        direction="row"
        sx={{ px: 2.5, py: 2, justifyContent: "space-between", alignItems: "center", width: "100%" }}
      >
        <Typography variant="h6">Columns</Typography>
        <CloseIconButton onClick={handleCancel} />
      </Stack>
      <Divider flexItem />
      <List sx={{ width: "100%", px: 0.5, py: 1, overflow: "auto" }}>
        <GlobalDndProvider>
          <DragAndDropList
            type="table_column"
            items={columns.visible}
            getItemKey={(column) => column.id}
            onDrop={handleItemDrop}
            renderItem={(column) => (
              <ColumnSelectionItem
                columnTitle={column.title}
                onHide={handleHideColumn(column.id)}
                disabled={isAddingColumn}
              />
            )}
            options={{ notDraggable: () => isAddingColumn }}
          />
        </GlobalDndProvider>
        {isAddingColumn && (
          <AddColumnSelect
            items={columns.available}
            onAdd={handleAddColumn}
            onCancel={() => setIsAddingColumn(false)}
          />
        )}
        {columns.available.length > 0 && (
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => setIsAddingColumn(true)}
            sx={{ ml: 2 }}
            disabled={isAddingColumn}
          >
            Add Column
          </Button>
        )}
      </List>
      <Divider flexItem />
      <Stack spacing={1} direction="row" sx={{ px: 2, py: 1.5 }}>
        <Button variant="contained" onClick={handleApply} disabled={!isDirty}>
          Apply
        </Button>
        <Button variant="text" color="secondary" onClick={handleCancel}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

export default ColumnSelectionEditor;
